import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { formatApiError } from '../lib/errors';
import { KeyRound, ArrowLeft, CheckCircle2, Mail, Loader2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BG = 'https://images.unsplash.com/photo-1694880912546-5d9619757fbb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwyfHxsYXR2aWElMjBsYW5kc2NhcGUlMjBmb2d8ZW58MHx8fHwxNzg0MTcwOTc1fDA&ixlib=rb-4.1.0&q=85';

export default function ForgotPasswordPage() {
  const { language } = useLanguage();
  const tr = (lv, en, ru) => (language === 'lv' ? lv : language === 'ru' ? ru : en);
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: email.trim().toLowerCase() });
      setSubmitted(true);
    } catch (err) {
      setError(formatApiError(err, tr('Kļūda. Mēģini vēlreiz.', 'Error. Try again.', 'Ошибка. Попробуйте снова.')));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center overflow-y-auto py-10 px-5" style={{ background: '#0A0D0B' }} data-testid="forgot-password-page">
      <div className="absolute inset-0 z-0">
        <img src={BG} alt="" className="w-full h-full object-cover opacity-30 scale-105" />
        <div className="absolute inset-0" style={{ background: 'radial-gradient(120% 90% at 50% 8%, rgba(10,13,11,0.15), rgba(10,13,11,0.9))' }} />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 w-full max-w-md rounded-[28px] border border-white/10 bg-[#0A0D0B]/70 backdrop-blur-2xl shadow-[0_8px_40px_rgba(0,0,0,0.6)] p-8 sm:p-10"
        style={{ fontFamily: 'Manrope, sans-serif' }}
      >
        <Link to="/login" className="inline-flex items-center gap-1.5 text-white/50 hover:text-white text-xs font-semibold mb-6 transition-colors">
          <ArrowLeft className="w-3.5 h-3.5" /> {tr('Atpakaļ uz ieiešanu', 'Back to login', 'Назад ко входу')}
        </Link>

        <div className="w-14 h-14 rounded-2xl bg-amber-500/15 flex items-center justify-center mb-5">
          <KeyRound className="w-7 h-7 text-amber-400" />
        </div>
        <h1 className="text-white text-3xl sm:text-4xl font-medium tracking-tight mb-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
          {tr('Aizmirsi paroli?', 'Forgot password?', 'Забыли пароль?')}
        </h1>
        <p className="text-white/60 text-sm mb-8">
          {tr('Nosūtīsim atiestatīšanas saiti uz Tavu e-pastu.', "We'll send a reset link to your email.", 'Отправим ссылку для сброса на вашу почту.')}
        </p>

        {submitted ? (
          <div className="space-y-5" data-testid="forgot-password-success">
            <div className="flex items-start gap-3 p-4 rounded-2xl bg-green-500/10 border border-green-500/30">
              <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-green-200/90">
                <p className="font-semibold mb-1 text-green-300">{tr('Pārbaudi savu iesūtni', 'Check your inbox', 'Проверьте почту')}</p>
                <p>{tr('Ja konts ar šo e-pastu eksistē, atiestatīšanas saite ir nosūtīta. Saite ir derīga 1 stundu.', 'If an account with this email exists, a reset link has been sent. The link is valid for 1 hour.', 'Если аккаунт с этой почтой существует, ссылка для сброса отправлена. Ссылка действительна 1 час.')}</p>
                <p className="mt-2 text-xs text-green-300/70">{tr('Neredzi vēstuli? Pārbaudi arī Spam mapi.', 'No email? Check your spam folder.', 'Нет письма? Проверьте папку «Спам».')}</p>
              </div>
            </div>
            <Link to="/login">
              <button className="w-full h-12 rounded-full bg-amber-500 text-[#0A0D0B] font-bold text-sm hover:bg-amber-400 transition-colors" style={{ boxShadow: '0 8px 30px rgba(255,159,28,0.35)' }}>
                {tr('Atgriezties uz ieiešanu', 'Back to login', 'Вернуться ко входу')}
              </button>
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="p-3 rounded-2xl bg-red-500/10 border border-red-500/30 text-red-300 text-sm">{error}</div>
            )}
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                placeholder={tr('tavs@epasts.lv', 'your@email.com', 'ваш@email.lv')}
                className="w-full h-12 pl-11 pr-4 rounded-2xl bg-white/5 border border-white/10 text-white placeholder-white/40 text-sm focus:border-amber-500/60 focus:bg-white/[0.07] focus:outline-none transition-colors"
                data-testid="forgot-email-input"
              />
            </div>
            <button
              type="submit"
              disabled={loading || !email}
              className="w-full h-12 rounded-full bg-amber-500 text-[#0A0D0B] font-bold text-sm flex items-center justify-center gap-2 hover:bg-amber-400 transition-colors disabled:opacity-60"
              style={{ boxShadow: '0 8px 30px rgba(255,159,28,0.35)' }}
              data-testid="forgot-submit-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : tr('Nosūtīt atiestatīšanas saiti', 'Send reset link', 'Отправить ссылку для сброса')}
            </button>
          </form>
        )}
      </motion.div>
    </div>
  );
}
